import { useMemo } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import type { PartRequestWithContext } from '@etnrlz/shared';
import { usePartRequests } from '../../lib/queries';
import { Badge, Loading } from '../../components/ui';
import { Icon } from '../../components/icons';
import { partRequestStatusMeta } from '../../components/status';
import { PartLogList, type LogEntryWithPart } from '../../components/PartLog';
import { formatDate, formatDateTime, formatMoney } from '../../lib/format';
import { carrierTrackingUrl } from '../../lib/tracking';

/**
 * Tela do Pedido (invoice): tudo de um OrderID num lugar — itens, rastreio (com
 * link ao vivo na transportadora) e o LOG de proveniência (o que foi atualizado
 * e de qual fonte: e-mail, Correios, FedEx…), agregando o histórico das peças.
 */
export function PedidoDetailPage() {
  const { orderId } = useParams();
  const { pathname } = useLocation();
  const base = pathname.startsWith('/shop') ? '/shop' : '/admin';
  const { data, isLoading } = usePartRequests();

  const items = useMemo(
    () => (data ?? []).filter((p) => (p.orderId ?? '').trim() === orderId),
    [data, orderId],
  );

  if (isLoading) return <Loading />;

  if (!items.length) {
    return (
      <div>
        <Link to={`${base}/compras`} className="text-sm text-accent hover:underline">
          ← Compras
        </Link>
        <p className="text-sm text-muted mt-4">Pedido não encontrado.</p>
      </div>
    );
  }

  const moedas = new Set(items.map((i) => i.moeda));
  const moeda = moedas.size === 1 ? [...moedas][0] : undefined;
  const subtotal = items.reduce((s, i) => s + (i.valor ?? 0) * (i.quantidade ?? 1), 0);
  const frete = items.reduce((s, i) => s + (i.frete ?? 0), 0);
  const carriers = [...new Set(items.map((i) => i.transportadora).filter(Boolean))];

  // Rastreios distintos do pedido.
  const seen = new Set<string>();
  const tracks: PartRequestWithContext[] = [];
  for (const p of items) {
    if (p.codigoRastreio && !seen.has(p.codigoRastreio)) {
      seen.add(p.codigoRastreio);
      tracks.push(p);
    }
  }

  // Log agregado (proveniência) de todas as peças do pedido.
  const log: LogEntryWithPart[] = items.flatMap((p) =>
    (p.historico ?? []).map((h) => ({ ...h, partTitulo: p.titulo })),
  );

  return (
    <div className="max-w-3xl space-y-4">
      <Link to={`${base}/compras`} className="text-sm text-accent hover:underline">
        ← Compras
      </Link>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-xl font-semibold flex items-center gap-2">
          <Icon name="cart" className="h-5 w-5 text-accent" /> Pedido #{orderId}
        </h1>
        <div className="text-sm">
          {moeda ? (
            <>
              <span className="num">{formatMoney(subtotal, moeda)}</span>
              {frete > 0 && <span className="num text-muted"> + frete {formatMoney(frete, moeda)}</span>}
              <span className="num font-medium"> = {formatMoney(subtotal + frete, moeda)}</span>
            </>
          ) : (
            <span className="text-muted">{items.length} item(s)</span>
          )}
        </div>
      </div>
      {carriers.length > 0 && (
        <div className="text-xs text-muted -mt-2">Transportadora: {carriers.join(', ')}</div>
      )}

      {/* Rastreio */}
      {tracks.length > 0 && (
        <div className="card p-4">
          <div className="text-xs uppercase tracking-wide text-muted mb-2">Rastreio</div>
          <ul className="space-y-3">
            {tracks.map((p) => {
              const url = carrierTrackingUrl(p.transportadora, p.codigoRastreio!);
              return (
                <li key={p.codigoRastreio} className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <Icon name="truck" className="h-4 w-4 text-muted shrink-0" />
                      <span className="num font-medium">{p.codigoRastreio}</span>
                      {p.transportadora && (
                        <span className="text-xs text-muted">· {p.transportadora}</span>
                      )}
                    </div>
                    {p.rastreioStatus && <div className="text-sm mt-0.5">{p.rastreioStatus}</div>}
                    {p.rastreioAtualizadoEm && (
                      <div className="text-[11px] text-muted">
                        atualizado {formatDateTime(p.rastreioAtualizadoEm)}
                      </div>
                    )}
                  </div>
                  {url && (
                    <a
                      href={url}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="text-accent hover:underline text-sm shrink-0 inline-flex items-center gap-1"
                    >
                      <Icon name="link" className="h-3.5 w-3.5" /> Rastrear
                    </a>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Itens */}
      <div className="card p-4">
        <div className="text-xs uppercase tracking-wide text-muted mb-2">
          Itens ({items.length})
        </div>
        <ul className="divide-y divide-border/50">
          {items.map((p) => {
            const meta = partRequestStatusMeta[p.status];
            return (
              <li key={p.id} className="py-2">
                <Link
                  to={`${base}/compras/${p.osId}/${p.id}`}
                  className="flex items-center gap-3 -mx-2 px-2 py-1 rounded hover:bg-surface-2/60 transition-colors"
                >
                  <div className="h-9 w-9 rounded bg-surface-2 overflow-hidden grid place-items-center shrink-0 border border-border">
                    {p.fotoUrl ? (
                      <img src={p.fotoUrl} alt="" className="h-full w-full object-cover" />
                    ) : (
                      <Icon name="box" className="h-4 w-4 text-muted" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">
                      {p.titulo ?? 'Peça'}
                      {p.quantidade > 1 && (
                        <span className="text-muted text-xs ml-1">×{p.quantidade}</span>
                      )}
                    </div>
                    {p.valor != null && (
                      <span className="num text-xs text-muted">{formatMoney(p.valor, p.moeda)}</span>
                    )}
                  </div>
                  {p.prazoEntrega && (
                    <span className="text-xs text-muted inline-flex items-center gap-1">
                      <Icon name="clock" className="h-3 w-3" /> {formatDate(p.prazoEntrega)}
                    </span>
                  )}
                  <Badge color={meta.color}>{meta.label}</Badge>
                </Link>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Log / proveniência */}
      <div className="card p-4">
        <div className="text-xs uppercase tracking-wide text-muted mb-3">
          Movimentações & origem (log)
        </div>
        <PartLogList entries={log} />
      </div>
    </div>
  );
}
